import { Module } from 'vuex'
import { FlowState } from './interface'
import { defaultBasic } from '@/utils/Flows'
import { Flow, Screen } from '@/interfaces/FlowInterfaces'
import BackendApi from '@/services/backend.service'
import _ from 'lodash'
import { RequestError, ValidationError } from '@/services/errorHandler.service'

const state: FlowState = {
  currentStep: '1',
  flowName: 'Flow',
  flowJson: defaultBasic,
  currentScreen: 0,
  isRenderingJsonEditor: true,
  stepTwoErrors: [],
  isCreatingFlow: false,
  showJsonEditor: false
}

const mutations = {
  SET_CURRENT_STEP(state: FlowState, newValue: string) {
    state.currentStep = newValue
  },
  SET_FLOW_JSON(state: FlowState, newValue: Flow) {
    state.flowJson = newValue
  },
  SET_FLOW_NAME(state: FlowState, newValue: string) {
    state.flowName = newValue
  },
  ADD_SCREEN(state: FlowState, newValue: Screen) {
    state.flowJson.screens.push(newValue)
  },
  SET_CURRENT_TITLE(state: FlowState, newValue: string) {
    state.flowJson.screens[state.currentScreen].title = newValue
  },
  SET_CURRENT_SCREEN(state: FlowState, newValue: number) {
    state.currentScreen = newValue
  },
  ADD_CHILD(state: FlowState, newChild: any) {
    const childrenArray = state.flowJson.screens[state.currentScreen].layout.children[0].children

    const footerIndex = findFooterIndex(childrenArray);

    if (footerIndex > -1) {
      childrenArray.splice(footerIndex, 0, newChild);
    } else {
      console.log('footer must be the final element in children array')
      childrenArray.push(newChild);
    }
  },
  DELETE_CHILD(state: FlowState, childIndex: number) {
    const childrenArray = state.flowJson.screens[state.currentScreen].layout.children[0].children

    if (childIndex >= 0 && childIndex < childrenArray.length) {
      childrenArray.splice(childIndex, 1)
    } else {
      console.error('Invalid child index:', childIndex)
    }
  },
  DELETE_SCREEN(state: FlowState, screenIndex: number) {
    if (screenIndex >= 0 && screenIndex < state.flowJson.screens.length) {
      state.flowJson.screens.splice(screenIndex, 1)
      if (state.currentScreen >= state.flowJson.screens.length) {
        state.currentScreen = Math.max(0, state.flowJson.screens.length - 1)
      }
    } else {
      console.error('Invalid screen index:', screenIndex)
    }
  },
  UPDATE_CHILD(state: FlowState, { childIndex, newChild }: { childIndex: number; newChild: any }) {
    const childrenArray = state.flowJson.screens[state.currentScreen].layout.children[0].children

    if (childIndex >= 0 && childIndex < childrenArray.length) {
      childrenArray[childIndex] = newChild
    } else {
      console.error('Invalid child index:', childIndex)
    }
  },
  SET_IS_RENDERING_JSON_EDITOR(state: FlowState, newValue: boolean) {
    state.isRenderingJsonEditor = newValue
  },
  SET_STEP_TWO_ERRORS(state: FlowState, newValue: any) {
    state.stepTwoErrors = newValue
  },
  SET_IS_CREATING_FLOW(state: FlowState, newValue: any) {
    state.isCreatingFlow = newValue
  },
  /* whatsapp_flows/SET_SHOW_JSON_EDITOR */
  SET_SHOW_JSON_EDITOR(state: FlowState, newValue: boolean) {
    state.showJsonEditor = newValue
  }
}

const getters = {
  currentScreen: (state: FlowState) => {
    return state.flowJson.screens[state.currentScreen]
  },

  currentFooter: (state: FlowState) => {
    const currentScreen = state.flowJson.screens[state.currentScreen]

    return currentScreen.layout.children[0].children.find(
      (child: any) => child.type === 'Footer'
    )
  }
}

const actions = {
  reloadJsonEditor({ commit }: { commit: any }) {
    commit('SET_IS_RENDERING_JSON_EDITOR', false)
    setTimeout(() => {
      commit('SET_IS_RENDERING_JSON_EDITOR', true)
    }, 10)
  },
  async createFlow({ commit }: { commit: any }) {
    try {
      commit('SET_IS_CREATING_FLOW', true)

      formatForScreenConections()
      formatForScreenChildsReplyLogic()

      const flow = {
        version: '6.0',
        screens: state.flowJson.screens
      }

      console.log('llegó aquí')
      const response = await BackendApi.post('/meta/whatsapp/flows', {
        flowJson: JSON.stringify(flow),
        name: state.flowName
      })

      commit('SET_IS_CREATING_FLOW', false)

      if (!response?.data?.success) {
        throw new RequestError(response)
      }

      return response

    } catch (error) {
      commit('SET_IS_CREATING_FLOW', false)
      console.error('An error occurred:', error)
      throw error
    }
  }
}

const findFooterIndex = (children: any[]): number => {
  return children.findIndex((child: any) => child.type === 'Footer');
}

const flowModule = {
  namespaced: true,
  getters,
  state,
  actions,
  mutations
}

const formatForScreenConections = (): void => {
  try {
    // Assign ids before to connect all screens
    state.flowJson.screens.forEach((screen, index) => {
      screen.id = 'SCREEN_' + String.fromCharCode(65 + index)
    })

    state.flowJson.screens.forEach((screen, index) => {
      const childrens = screen.layout.children[0].children

      childrens.forEach((child, cIndex) => {
        try {
          if (child.type === 'Image') {
            delete child.srcForPreview // This is only for the front
          }

          if (child.type === 'Footer') {
            const previousScreen = state.flowJson.screens[cIndex - 1] ?? null

            if (previousScreen) {
              const previousChildrens = previousScreen?.layout.children[0].children
              const previousFooter = previousChildrens[previousChildrens.length - 1]

              if (!('on-click-action' in previousFooter)) {
                throw new Error('footer expected on click action propertie')
              }
            }

            if (index !== state.flowJson.screens.length - 1) {
              screen.terminal = false
              screen.data = {}
              child['on-click-action'].name = 'navigate'
              child['on-click-action'].payload = {}

              child['on-click-action'].next = {
                type: 'screen',
                name: state.flowJson.screens[index + 1].id 
              }
            } else {
              screen.terminal = true
              screen.data = {}
              child['on-click-action'].name = 'complete'
              child['on-click-action'].payload = {}
            }
          }
        } catch (childError) {
          console.error(`Error processing child at index ${cIndex}:`, childError)
        }
      })
    })
  } catch (screenError) {
    console.error('Error processing screen:', screenError)
  }
}
const formatForScreenChildsReplyLogic = (): void => {
  try {
    state.flowJson.screens.forEach((screen: any, screenIndex: number) => {
      const childrenArray = screen.layout.children[0].children
      
      childrenArray.forEach((child: any, cIndex: number) => {
        if (child.type !== 'Image' &&
            child.type !== 'TextHeading' &&
            child.type !== 'TextSubheading' &&
            child.type !== 'TextBody' &&
            child.type !== 'TextCaption' &&
            child.type !== 'Footer' &&
            child.name
        ) {
          const formattedLabel = _.snakeCase(_.deburr(child.label))

          child.name = 'screen_' + screenIndex + '_child_' + cIndex + '_type_' + child.type + '_label_' + formattedLabel

          if (child.type === 'RadioButtonsGroup' || child.type === 'CheckboxGroup' || child.type === 'Dropdown') {
            child['data-source'].forEach((element: any, sIndex: number) => {
              const formattedTitle = _.snakeCase(_.deburr(element.title))
              const formattedName = 'selection_' + sIndex + '_' + formattedTitle

              element.id = formattedName
            })
          }
        }
      })
    })
    
    const lastScreen = state.flowJson.screens[state.flowJson.screens.length - 1]
    const childrenArray = lastScreen.layout.children[0].children

    const footer = childrenArray[childrenArray.length - 1]

    if (footer.type === 'Footer' && footer['on-click-action'].name === 'complete') {
      state.flowJson.screens.forEach((screenV: any, screenIndex: number) => {
        footer['on-click-action'].payload[screenV.id] = {
          title: screenV.title,
          index: screenIndex,
          childs: []
        }

        screenV.layout.children[0].children.forEach((childZ: any, cIndex: number) => {
          const child: any = childZ

          if (
            child.type !== 'Image' &&
            child.type !== 'TextHeading' &&
            child.type !== 'TextSubheading' &&
            child.type !== 'TextBody' &&
            child.type !== 'TextCaption' &&
            child.type !== 'Footer'
          ) {
            const fieldData = {
              id: `field_${cIndex}`,
              name: child.name,
              label: child.label,
              value: `\${screen.${screenV.id}.form.${child.name}}`,
              ...(child.type === 'RadioButtonsGroup' ||
                child.type === 'CheckboxGroup' ||
                child.type === 'Dropdown'
                ? { selectPayload: child['data-source'] }
                : {})
            }

            footer['on-click-action'].payload[screenV.id].childs.push(fieldData)
          }
        })
      })
    } else {
      console.error('footer expected as last element')
    }
  } catch (screenError) {
    console.error('Error processing screen:', screenError)
  }
}
export default flowModule
